// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames/bind';
import { FormValidator } from '../../../../utils/form/index';
import update from 'immutability-helper';
import SuccessErrorModal from '../../../../elements/SuccessErrorModal/SuccessErrorModal';
import PhoneForm from './PhoneForm/PhoneForm';

import commonStyle from '../../PreferencesViewCommon.scss';
import boxStyle from '../../../../assets/styles/box.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import inputStyle from '../../../../assets/styles/input.scss';
import formStyle from '../../../../assets/styles/form.scss';
import phoneImg from '../../../../assets/img/preferences/security/phone.png'
import style from './Security.scss';

import type { HTTPError } from '../../../../error';
import type { FormValidationResult } from '../../../../utils/form';
import type { Option } from '../../../../elements/Select/Select';
import type { User } from '../../../../entities';
import type { LocalValuesState } from '../../../local-values/duck';

type Props = {
  editSuccessful?: boolean;
  user: User;
  editEmail: (email: string) => void;
  isPuttingEmail?: boolean;
  putEmailError?: HTTPError;
  postPhone: (phone: string, country: string, enableSmsNotifications: boolean) => void;
  isPostingPhone?: boolean;
  postPhoneError?: HTTPError;
  postCode: (code: string) => void;
  isPostingCode?: boolean;
  successfulVerification?: boolean;
  postCodeError?: HTTPError;
  resetAlertState: () => void;
  localValues: LocalValuesState;
};

type State = {
  country: {
    value: string;
    errors?: string[];
  };
  email: {
    value: string;
    pristine: boolean;
    errors?: string[];
  };
  phone: {
    value: string;
    pristine: boolean;
    errors?: string[];
  };
  code: {
    value: string;
    errors?: string[];
  };
  willVerify: boolean;
  agreeToReceiveSMS: boolean;
  modalVisible: boolean;
};

const messages = defineMessages({
  title: {
    id: 'preferences.security.document.title',
  },
  description: {
    id: 'preferences.security.document.description',
  },
  verify: {
    one: {
      id: 'preferences.security.phone.verify.subtitle.one',
    },
    two: {
      id: 'preferences.security.phone.verify.subtitle.two',
    },
  },
});

export default class Security extends Component<Props, State> {
  formValidatorEmail: FormValidator;
  formValidatorPhone: FormValidator;
  formValidatorCode: FormValidator;

  constructor(props: Props) {
    super(props);
    const { user } = props;
    this.formValidatorEmail = new FormValidator({
      email: {
        required: 'required',
        emailFormat: 'emailFormat',
      },
    });
    this.formValidatorPhone = new FormValidator({
      country: {
        required: 'required',
      },
      phone: {
        required: 'required',
      },
    });
    this.formValidatorCode = new FormValidator({
      code: {
        required: 'required',
      },
    });
    this.state = {
      email: { value: user.email || '', pristine: true },
      phone: { value: user.phone || '', pristine: true },
      country: { value: user.phoneCountry ? user.phoneCountry.code : '' },
      code: { value: '' },
      willVerify: false,
      modalVisible: false,
      agreeToReceiveSMS: false,
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { editSuccessful, successfulVerification, postCodeError } = this.props;
    const { willVerify } = this.state;
    if (!prevProps.editSuccessful && editSuccessful) {
      this.setState((state: State) => ({
        email: update(state.email, { pristine: { $set: true } }),
        phone: update(state.phone, { pristine: { $set: true } }),
        modalVisible: true,
      }));
      // We show the `section` to enter the code 
      if (!willVerify) this.handleWillVerify();
    }
    if (!prevProps.successfulVerification && successfulVerification) {
      this.setState((state: State) => ({
        email: update(state.email, { pristine: { $set: true } }),
        phone: update(state.phone, { pristine: { $set: true } }),
        willVerify: false,
      }));
    }
    if (!!postCodeError && !prevProps.postCodeError &&
      !!postCodeError.data && postCodeError.data.name === 'VerificationTokenError') {
      this.setState((prevState: State) => ({
        code: update(prevState.code, {
          errors: { $set: ['tokenError'] },
        }),
      }));
    }
  }

  componentWillUnmount() {
    const { resetAlertState } = this.props;
    resetAlertState();
  }

  handleChange(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const { name, value, rawValue } = e.target;
      if (name === 'code') {
        this.setState({
          code: { value },
        });
      } else if (name === 'phone') {
        const pristine = this.props.user[name] === rawValue;
        this.setState({ [name]: { value, pristine } });
      } else {
        const pristine = this.props.user[name] === value;
        this.setState({ [name]: { value, pristine } });
      }
    }
  }

  handleFocus(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState((state: State) => ({ [name]: update(state[name], { value: { $set: value } }) }));
    }
  }

  handleBlurEmail(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      const errors = this.formValidatorEmail.validateField(name, value);
      if (errors.length) {
        this.setState({ [name]: { value, errors } });
      } else {
        this.setState((state: State) => ({ [name]: update(state[name], { value: { $set: value } }) }));
      }
    }
  }

  handleSubmitEmail(e: Event) {
    e.preventDefault();
    const { editEmail } = this.props;
    const emailValue = this.state.email.value;
    const errors = this.formValidatorEmail.validateForm({
      email: emailValue,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) =>
        this.setState({ [error.field]: { ...this.state[error.field], errors: error.errors, }, }));
    } else {
      editEmail(emailValue);
    }
  }

  handleChangeCountry(option: Option) {
    this.setState({
      country: {
        value: `${option.value}`,
      }
    });
  }

  handleSubmitPhone = (e?: Event, resend?: boolean) => {
    if (e) e.preventDefault();
    const { postPhone } = this.props;
    const phoneValue = this.state.phone.value;
    const countryValue = this.state.country.value;
    const errors = this.formValidatorPhone.validateForm({
      phone: phoneValue,
      country: countryValue,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) =>
        this.setState({ [error.field]: { ...this.state[error.field], errors: error.errors, }, }));
    } else {
      // We set the last parameter
      // if we resend the code `resend` is true, else whatever the user chose
      postPhone(phoneValue, countryValue, resend || this.state.agreeToReceiveSMS);
    }
  };

  handleWillVerify = (e?: Event) => {
    if (e) e.preventDefault();
    // If I'm not waiting to input the code then you are
    // at the previous view and should request another code.
    if (!this.state.willVerify && this.props.user.phone) {
      this.handleSubmitPhone();
    }
    this.setState((prevState: State) => ({ willVerify: !prevState.willVerify }));
  };

  handleSubmitCode = (e: Event) => {
    e.preventDefault();
    const { postCode } = this.props;
    const errors = this.formValidatorCode.validateForm({
      code: this.state.code.value,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) =>
        this.setState({ [error.field]: { ...this.state[error.field], errors: error.errors } }));
    } else {
      postCode(this.state.code.value);
    }
  };

  handleCloseModal() {
    this.setState({ modalVisible: false });
  }

  handleAcceptReceiveSMS = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      this.setState((state: State) => ({
        agreeToReceiveSMS: !state.agreeToReceiveSMS,
      }));
    }
  };

  render() {
    const { isPuttingEmail, isPostingPhone, isPostingCode, localValues, user } = this.props;
    const { country, email, phone, code, willVerify, modalVisible, agreeToReceiveSMS } = this.state;
    const emailError = email.errors && FormValidator.buildErrorLabel(email.errors, {
      emailFormat: 'preferences.security.email.error.format',
      required: 'preferences.security.email.error.required',
    });
    const codeError = code.errors && FormValidator.buildErrorLabel(code.errors, {
      tokenError: 'preferences.security.phone.verify.token.error.tokenError',
      required: 'preferences.security.phone.verify.token.error.required',
    });
    return <Fragment>
      <Helmet>
        <title>{this.context.intl.formatMessage(messages.title)}</title>
        <meta name="description" content={this.context.intl.formatMessage(messages.description)}/>
      </Helmet>
      {!user.social.facebook && <section className={classNames(boxStyle.box, commonStyle.contentWrap)}>
        <FormattedMessage id="preferences.security.email.title">{
          txt => <h2 className={classNames(boxStyle.boxTitle, boxStyle.smallSpace)}>{txt}</h2>
        }</FormattedMessage>
        <FormattedMessage id="preferences.security.email.subtitle">{
          txt => <p className={boxStyle.subtitle}>{txt}</p>
        }</FormattedMessage>
        <form className={classNames(formStyle.formWrap, commonStyle.inputs)}>
          <section className={classNames(formStyle.row, formStyle.inputRow)}>
            <FormattedMessage id="preferences.security.email.label">{
              txt => <label className={formStyle.label}>{txt}</label>
            }</FormattedMessage>
            <input name="email"
                   className={classNames(inputStyle.input, { [inputStyle.red]: email.errors && email.errors.length },
                     { [inputStyle.lightBlue]: !!email.value })}
                   disabled={isPuttingEmail}
                   onChange={this.handleChange.bind(this)}
                   onBlur={this.handleBlurEmail.bind(this)}
                   onFocus={this.handleFocus.bind(this)}
                   value={email.value}/>
            {emailError}
          </section>

          <section className={classNames(formStyle.row, formStyle.inputRow, commonStyle.button)}>
            <FormattedMessage id="preferences.security.saveChange">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                             disabled={isPuttingEmail || email.pristine}
                             onClick={this.handleSubmitEmail.bind(this)}>{txt}</button>
            }</FormattedMessage>
          </section>
        </form>
      </section>}
      {(!willVerify || user.isPhoneVerified) &&
      <section className={classNames(boxStyle.box, commonStyle.contentWrap)}>
        <FormattedMessage id="preferences.security.phone.title">{
          txt => <h2 className={classNames(boxStyle.boxTitle, boxStyle.smallSpace)}>{txt}</h2>
        }</FormattedMessage>
        <FormattedMessage id="preferences.security.phone.subtitle">{
          txt => <p className={boxStyle.subtitle}>{txt}</p>
        }</FormattedMessage>
        <PhoneForm handleChangeCountry={this.handleChangeCountry.bind(this)}
                   handleChange={this.handleChange.bind(this)}
                   handleFocus={this.handleFocus.bind(this)}
                   handleSubmitPhone={this.handleSubmitPhone}
                   handleWillVerify={this.handleWillVerify}
                   handleAcceptReceiveSMS={this.handleAcceptReceiveSMS}
                   isPostingPhone={isPostingPhone}
                   country={country}
                   phone={phone}
                   color="light-blue"
                   localValues={localValues}
                   agreeToReceiveSMS={agreeToReceiveSMS}
                   isPhoneVerified={user.isPhoneVerified}/>
      </section>}
      {!!user.phone && willVerify &&
      <section className={classNames(boxStyle.box, commonStyle.contentWrap, style.verifyBox)}>
        <FormattedMessage id="preferences.security.phone.verify.title.first">{
          txt => <h2 className={classNames(boxStyle.boxTitle, boxStyle.smallSpace)}>{txt}</h2>
        }</FormattedMessage>
        <p className={boxStyle.subtitle}>
          {`${this.context.intl.formatMessage(messages.verify.one)} `}
          <b>{user.phone}</b>
          {` ${this.context.intl.formatMessage(messages.verify.two)}`}
        </p>
        <img className={style.verifyImg}
             src={phoneImg}
             alt="phone"/>
        <FormattedMessage id="preferences.security.phone.verify.resend">{
          txt => <a className={style.lightBlue}
                    onClick={(e: Event) => this.handleSubmitPhone(e, true)}>{txt}</a>
        }</FormattedMessage>
        <FormattedMessage id="preferences.security.phone.verify.changeNumber">{
          txt => <a className={style.lightBlue}
                    onClick={this.handleWillVerify}>{txt}</a>
        }</FormattedMessage>
        <FormattedMessage id="preferences.security.phone.verify.later">{
          txt => <a className={style.later}
                    onClick={this.handleWillVerify}>{txt}</a>
        }</FormattedMessage>
        <FormattedMessage id="preferences.security.phone.verify.title.second">{
          txt => <h2 className={boxStyle.boxTitle}>{txt}</h2>
        }</FormattedMessage>
        <form className={classNames(formStyle.formWrap, commonStyle.inputs)}>
          <section className={classNames(formStyle.row, formStyle.inputRow)}>
            <FormattedMessage id="preferences.security.phone.verify.token.label">{
              txt => <label className={formStyle.label}>{txt}:</label>
            }</FormattedMessage>
            <input type="tel"
                   name="code"
                   className={classNames(inputStyle.input, { [inputStyle.red]: code.errors && code.errors.length },
                     { [inputStyle.lightBlue]: !!code.value })}
                   disabled={isPostingCode}
                   onChange={this.handleChange.bind(this)}
                   onFocus={this.handleFocus.bind(this)}
                   value={code.value}/>
            {codeError}
          </section>
          <section className={classNames(formStyle.row, formStyle.inputRow, commonStyle.button)}>
            <FormattedMessage id="preferences.security.phone.verify.token.button">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                             disabled={isPostingCode}
                             onClick={this.handleSubmitCode}>{txt}</button>
            }</FormattedMessage>
          </section>
        </form>
      </section>}
      <SuccessErrorModal key="modal"
                         isOpen={modalVisible}
                         handleCloseModal={this.handleCloseModal.bind(this)}
                         titleInt={willVerify ? "preferences.modal.successfulSend" : "preferences.modal.successfulUpdate.title"}
                         color="lightBlue"
                         success={true}/>
    </Fragment>
  }
}

Security.propTypes = {
  editEmail: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  editSuccessful: PropTypes.bool,
  isPuttingEmail: PropTypes.bool,
  putEmailError: PropTypes.object,
  postPhone: PropTypes.func.isRequired,
  isPostingPhone: PropTypes.bool,
  postPhoneError: PropTypes.object,
  postCode: PropTypes.func.isRequired,
  isPostingCode: PropTypes.bool,
  successfulVerification: PropTypes.bool,
  postCodeError: PropTypes.object,
  resetAlertState: PropTypes.func.isRequired,
  localValues: PropTypes.object.isRequired,
};

Security.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/preferences/security/components/Security.jsx